/**
 * Campaign More Module
 * 初期4件表示 + もっと見るで4件ずつ追加表示
 */
export class CampaignMore {
  constructor() {
    this.root = document.querySelector('.l-campaign');
    if (!this.root) {
      return;
    }

    this.list = this.root.querySelector('.l-campaign__list');
    this.items = Array.from(this.list ? this.list.children : []);
    this.more = this.root.querySelector('.l-campaign__more');
    this.button = this.root.querySelector('.l-campaign__more-btn');

    if (!this.list || !this.more || !this.button || this.items.length === 0) {
      return;
    }

    this.step = 4; 
    this.visibleCount = this.step;
    this.prevVisibleCount = 0;
    this.onMoreClick = this.handleMoreClick.bind(this);
    this.init();
  }

  init() {
    this.render();
    this.button.addEventListener('click', this.onMoreClick);
  }

  destroy() {
    if (this.button && this.onMoreClick) {
      this.button.removeEventListener('click', this.onMoreClick);
    }
  }

  handleMoreClick(event) {
    if (this.button.tagName === 'A') {
      event.preventDefault();
    }
    this.showMore();
  }

  showMore() {
    this.prevVisibleCount = this.visibleCount;
    this.visibleCount += this.step;
    this.render(true);
  }

  render(animateNew = false) {
    this.items.forEach((item, index) => {
      const itemVisible = index < this.visibleCount;
      const wasVisible = !item.hidden;

      item.hidden = !itemVisible;

      if (!itemVisible) {
        item.classList.remove('is-enter');
        item.classList.remove('is-enter-active');
        return;
      }

      const isNewItem = animateNew && !wasVisible && index >= this.prevVisibleCount;
      if (isNewItem) {
        item.classList.add('is-enter');
        requestAnimationFrame(() => {
          item.classList.add('is-enter-active');
        });
      } else {
        item.classList.remove('is-enter');
        item.classList.add('is-enter-active');
      }
    });

    if (this.visibleCount >= this.items.length) {
      this.more.hidden = true;
      return;
    }

    this.more.hidden = false;
  }
}

export class CampaignSlider {
  constructor() {
    this.roots = Array.from(document.querySelectorAll('.l-campaign-slider'));
    if (this.roots.length === 0) {
      return;
    }

    this.instances = this.roots
      .map((root) => this.createInstance(root))
      .filter((instance) => instance !== null);

    this.mediaQuery = window.matchMedia('(max-width: 767px)');
    this.onBreakpointChange = () => this.instances.forEach((inst) => this.handleBreakpointChange(inst));

    if (typeof this.mediaQuery.addEventListener === 'function') {
      this.mediaQuery.addEventListener('change', this.onBreakpointChange);
    } else if (typeof this.mediaQuery.addListener === 'function') {
      this.mediaQuery.addListener(this.onBreakpointChange);
    }
  }

  createInstance(root) {
    if (root.dataset.campaignSliderInitialized === 'true') {
      return null;
    }

    const list = root.querySelector('.l-campaign-slider__list');
    const items = Array.from(root.querySelectorAll('.l-campaign-slider__item'));

    if (!list || items.length === 0) {
      return null;
    }

    root.dataset.campaignSliderInitialized = 'true';

    const nav = this.createNavigation(items);
    root.appendChild(nav.container);

    const instance = {
      root,
      list,
      items,
      currentIndex: 0,
      targetIndex: 0,
      nav,
      scrollRaf: null,
      scrollEndTimer: null,
      scrollEndChecks: 0,
      isProgrammaticScroll: false,
    };

    instance.onScroll = () => this.handleScroll(instance);
    instance.onScrollEnd = () => this.finishProgrammaticScroll(instance);
    instance.onUserScrollStart = () => this.cancelProgrammaticScroll(instance);
    instance.onPrevClick = () => this.scrollToIndex(instance, instance.currentIndex - 1);
    instance.onNextClick = () => this.scrollToIndex(instance, instance.currentIndex + 1);
    instance.onThumbClick = (event) => this.handleThumbClick(instance, event);

    list.addEventListener('scroll', instance.onScroll, { passive: true });
    list.addEventListener('scrollend', instance.onScrollEnd);
    list.addEventListener('pointerdown', instance.onUserScrollStart, { passive: true });
    list.addEventListener('touchstart', instance.onUserScrollStart, { passive: true });
    list.addEventListener('wheel', instance.onUserScrollStart, { passive: true });
    nav.prevButton.addEventListener('click', instance.onPrevClick);
    nav.nextButton.addEventListener('click', instance.onNextClick);
    nav.thumbList.addEventListener('click', instance.onThumbClick);

    this.sync(instance, false);

    return instance;
  }

  createNavigation(items) {
    const container = document.createElement('div');
    container.className = 'l-campaign-slider__nav';

    const thumbList = document.createElement('ul');
    thumbList.className = 'l-campaign-slider__thumbs';

    items.forEach((item, index) => {
      const itemImage = item.querySelector('img');
      const thumbItem = document.createElement('li');
      thumbItem.className = 'l-campaign-slider__thumb-item';

      const thumbButton = document.createElement('button');
      thumbButton.className = 'l-campaign-slider__thumb';
      thumbButton.type = 'button';
      thumbButton.dataset.index = String(index);
      thumbButton.setAttribute('aria-label', `${index + 1}枚目を表示`);

      if (itemImage) {
        const thumbImage = document.createElement('img');
        thumbImage.className = 'l-campaign-slider__thumb-image';
        thumbImage.src = itemImage.getAttribute('src') || '';
        thumbImage.alt = '';
        thumbImage.setAttribute('aria-hidden', 'true');
        thumbButton.appendChild(thumbImage);
      }

      thumbItem.appendChild(thumbButton);
      thumbList.appendChild(thumbItem);
    });

    const footer = document.createElement('div');
    footer.className = 'l-campaign-slider__footer';

    const indicator = document.createElement('span');
    indicator.className = 'l-campaign-slider__indicator';

    const indicatorThumb = document.createElement('span');
    indicatorThumb.className = 'l-campaign-slider__indicator-thumb';
    indicator.appendChild(indicatorThumb);

    const arrows = document.createElement('div');
    arrows.className = 'l-campaign-slider__arrows';

    const prevButton = document.createElement('button');
    prevButton.className = 'l-campaign-slider__arrow l-campaign-slider__arrow--prev';
    prevButton.type = 'button';
    prevButton.setAttribute('aria-label', '前へ');

    const nextButton = document.createElement('button');
    nextButton.className = 'l-campaign-slider__arrow l-campaign-slider__arrow--next';
    nextButton.type = 'button';
    nextButton.setAttribute('aria-label', '次へ');

    arrows.append(prevButton, nextButton);
    footer.append(indicator, arrows);
    container.append(thumbList, footer);

    return {
      container,
      thumbList,
      thumbButtons: Array.from(thumbList.querySelectorAll('.l-campaign-slider__thumb')),
      indicator,
      indicatorThumb,
      prevButton,
      nextButton,
    };
  }

  clampIndex(instance, index) {
    return Math.max(0, Math.min(index, instance.items.length - 1));
  }

  getItemLeft(instance, index) {
    const item = instance.items[index];
    if (!item) {
      return 0;
    }

    const itemRect = item.getBoundingClientRect();
    const listRect = instance.list.getBoundingClientRect();
    return itemRect.left - listRect.left + instance.list.scrollLeft;
  }

  getNearestIndex(instance) {
    const currentLeft = instance.list.scrollLeft;
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    instance.items.forEach((item, index) => {
      const distance = Math.abs(currentLeft - this.getItemLeft(instance, index));
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  }

  scrollToIndex(instance, index, smooth = true) {
    const targetIndex = this.clampIndex(instance, index);
    instance.currentIndex = targetIndex;
    instance.targetIndex = targetIndex;
    // Keep controls locked to the clicked target during smooth scroll.
    // Direct user input cancels this mode and syncs to the nearest slide.
    instance.isProgrammaticScroll = smooth;
    instance.scrollEndChecks = 0;

    if (instance.scrollEndTimer) {
      clearTimeout(instance.scrollEndTimer);
      instance.scrollEndTimer = null;
    }

    const left = this.getItemLeft(instance, targetIndex);
    instance.list.scrollTo({
      left,
      behavior: smooth ? 'smooth' : 'auto',
    });

    this.sync(instance);

    if (smooth) {
      this.scheduleProgrammaticScrollEnd(instance);
    }
  }

  handleThumbClick(instance, event) {
    const button = event.target.closest('.l-campaign-slider__thumb');
    if (!button) {
      return;
    }

    this.scrollToIndex(instance, Number(button.dataset.index));
  }

  handleScroll(instance) {
    if (instance.scrollRaf) {
      cancelAnimationFrame(instance.scrollRaf);
    }

    instance.scrollRaf = requestAnimationFrame(() => {
      instance.scrollRaf = null;

      if (instance.isProgrammaticScroll) {
        this.scheduleProgrammaticScrollEnd(instance);

        if (this.isAtTarget(instance)) {
          this.finishProgrammaticScroll(instance);
        }
        return;
      }

      instance.currentIndex = this.clampIndex(instance, this.getNearestIndex(instance));
      this.sync(instance);
    });
  }

  handleBreakpointChange(instance) {
    this.scrollToIndex(instance, instance.currentIndex, false);
  }

  scheduleProgrammaticScrollEnd(instance) {
    if (instance.scrollEndTimer) {
      clearTimeout(instance.scrollEndTimer);
    }

    instance.scrollEndTimer = setTimeout(() => {
      instance.scrollEndTimer = null;

      if (!instance.isProgrammaticScroll) {
        return;
      }

      if (this.isAtTarget(instance)) {
        this.finishProgrammaticScroll(instance);
        return;
      }

      instance.scrollEndChecks += 1;
      if (instance.scrollEndChecks >= 10) {
        instance.list.scrollTo({
          left: this.getItemLeft(instance, instance.targetIndex),
          behavior: 'auto',
        });
        this.finishProgrammaticScroll(instance);
        return;
      }

      this.scheduleProgrammaticScrollEnd(instance);
    }, 160);
  }

  isAtTarget(instance) {
    const targetLeft = this.getItemLeft(instance, instance.targetIndex);
    return Math.abs(instance.list.scrollLeft - targetLeft) <= 2;
  }

  finishProgrammaticScroll(instance) {
    if (!instance.isProgrammaticScroll) {
      return;
    }

    if (instance.scrollEndTimer) {
      clearTimeout(instance.scrollEndTimer);
      instance.scrollEndTimer = null;
    }

    instance.isProgrammaticScroll = false;
    instance.scrollEndChecks = 0;
    instance.currentIndex = instance.targetIndex;
    this.sync(instance, false);
  }

  cancelProgrammaticScroll(instance) {
    if (!instance.isProgrammaticScroll) {
      return;
    }

    if (instance.scrollEndTimer) {
      clearTimeout(instance.scrollEndTimer);
      instance.scrollEndTimer = null;
    }

    instance.isProgrammaticScroll = false;
    instance.scrollEndChecks = 0;
    instance.list.scrollTo({
      left: instance.list.scrollLeft,
      behavior: 'auto',
    });
    instance.currentIndex = this.clampIndex(instance, this.getNearestIndex(instance));
    instance.targetIndex = instance.currentIndex;
    this.sync(instance, false);
  }

  alignActiveThumb(instance, smooth = true) {
    const activeThumb = instance.nav.thumbButtons[instance.currentIndex];
    if (!activeThumb) {
      return;
    }

    const thumbItem = activeThumb.closest('.l-campaign-slider__thumb-item');
    if (!thumbItem) {
      return;
    }

    const thumbListRect = instance.nav.thumbList.getBoundingClientRect();
    const thumbItemRect = thumbItem.getBoundingClientRect();
    const scrollLeft = thumbItemRect.left - thumbListRect.left + instance.nav.thumbList.scrollLeft;

    instance.nav.thumbList.scrollTo({
      left: scrollLeft,
      behavior: smooth ? 'smooth' : 'auto',
    });
  }

  updateIndicator(instance) {
    if (!instance.nav.indicatorThumb) {
      return;
    }

    const total = instance.items.length;
    const thumbWidth = total > 1 ? Math.max((instance.currentIndex + 1) / total * 100, 5) : 100;
    instance.nav.indicatorThumb.style.width = `${thumbWidth}%`;
    instance.nav.indicatorThumb.style.left = '0';
  }

  sync(instance, smooth = true) {
    instance.items.forEach((item, index) => {
      item.classList.toggle('is-active', index === instance.currentIndex);
    });

    instance.nav.thumbButtons.forEach((button, index) => {
      const isActive = index === instance.currentIndex;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    instance.nav.prevButton.disabled = instance.currentIndex <= 0;
    instance.nav.nextButton.disabled = instance.currentIndex >= instance.items.length - 1;
    this.updateIndicator(instance);

    this.alignActiveThumb(instance, smooth);
  }

  destroy() {
    if (!this.instances) {
      return;
    }

    this.instances.forEach((instance) => {
      instance.list.removeEventListener('scroll', instance.onScroll);
      instance.list.removeEventListener('scrollend', instance.onScrollEnd);
      instance.list.removeEventListener('pointerdown', instance.onUserScrollStart);
      instance.list.removeEventListener('touchstart', instance.onUserScrollStart);
      instance.list.removeEventListener('wheel', instance.onUserScrollStart);
      instance.nav.prevButton.removeEventListener('click', instance.onPrevClick);
      instance.nav.nextButton.removeEventListener('click', instance.onNextClick);
      instance.nav.thumbList.removeEventListener('click', instance.onThumbClick);

      if (instance.scrollRaf) {
        cancelAnimationFrame(instance.scrollRaf);
      }

      if (instance.scrollEndTimer) {
        clearTimeout(instance.scrollEndTimer);
      }

      instance.nav.container.remove();
      instance.root.removeAttribute('data-campaign-slider-initialized');
    });

    if (typeof this.mediaQuery.removeEventListener === 'function') {
      this.mediaQuery.removeEventListener('change', this.onBreakpointChange);
    } else if (typeof this.mediaQuery.removeListener === 'function') {
      this.mediaQuery.removeListener(this.onBreakpointChange);
    }
  }
}
